import localforage from 'localforage'

interface NoteEntry {
  id: string
  title: LangString
}

const FORAGE_NOTE_LIST_KEY = 'jps-local-note-list-v1'

export const useNoteStore = defineStore(
  'note',
  () => {

    const list: Ref<NoteEntry[]> = ref()
    const primary: Ref<Curation> = ref()

    if (import.meta.client) {
      localforage.getItem<NoteEntry[]>(FORAGE_NOTE_LIST_KEY).then(async (value) => {
        if (value && value.length > 0) {
          list.value = value
          localforage.getItem<Curation>(list.value[0].id).then((note) => {
            primary.value = note
          })
        } else {
          await initNote()
        }
      })

      const initNote = async () => {
        const note = createNote({
          ja: '最初のノート',
          en: 'First note',
        })
        const notes = [{ id: note.id, title: note.title }]
        await saveNote(note)
        await saveList(notes)
        primary.value = note
        list.value = notes
      }

      const clearNotes = () => {
        list.value.forEach((entry) => {
          localforage.removeItem(entry.id)
        })
        initNote()
      }

      const saveNote = (note: Curation): Promise<Curation> => {
        return localforage.setItem(note.id, cloneJson(note))
      }

      const saveList = (targetList: NoteEntry[]): Promise<NoteEntry[]> => {
        return localforage.setItem(FORAGE_NOTE_LIST_KEY, cloneJson(targetList))
      }

      const createNote = (title: LangString): Curation => {
        const note: Curation = {}
        note.id = hashId()
        note.title = title
        note.curationType = 'NOTE'
        note.parts = [
          { type: 'jps-curation-text', ja: null, en: null, cpid: luId() },
          createNewList(),
        ]
        return note
      }

      const createNewList = (): PartList => {
        return {
          cpid: luId(),
          type: 'jps-curation-list',
          viewType: 'bullet',
          csId: systemCrossSearchId(),
          openActionType: 'link',
          parts: [],
        }
      }

      const getNote = (id: string): Promise<Curation> => {
        return localforage.getItem(id)
      }

      const changePrimary = async (id: string): Promise<any> => {
        primary.value = await localforage.getItem<Curation>(id)
        if (primary.value != null) {
          const notes = list.value.filter((e) => e.id !== id)
          notes.unshift({id: id, title: primary.value.title})
          list.value = notes
          await saveList(notes)
        }
      }

      const createNewNoteByTitle = async (title: LangString): Promise<Curation> => {
        return createNewNote(createNote(title))
      }

      const createNewNote = async (note: Curation): Promise<Curation> => {
        await saveNote(note)
        list.value.push({ id: note.id, title: note.title })
        await saveList(list.value)
        return note
      }

      const updateNote = async (note: Curation): Promise<Curation> => {
        const entry = list.value.find((e) => e.id === note.id)
        entry.title = note.title
        await saveList(list.value)
        return saveNote(note)
      }

      const deleteNote = async (id: string): Promise<any> => {
        await localforage.removeItem(id)
        list.value = list.value.filter((e) => e.id !== id)
        if (list.value.length == 0) {
         await initNote()
        } else {
          await changePrimary(list.value[0].id)
        }
      }

      //fav系
      const addFavorite = async (key: Key, id: string, image: ImageSelection) => {
        if (isFavorite(key, id) && !image) return
        const tmpList = getList(primary.value)
        const newFavorite = {
          id: id,
          cpid: luId(),
          type: 'jps-curation-list-' + key,
        }
        if (image) newFavorite['image'] = image
        tmpList.parts.push(newFavorite)
        await saveNote(primary.value)
      }

      const removeFavorite = async (key: Key, id: string) => {
        if (primary.value) {
          for (let i = 0; i < primary.value.parts.length; i++) {
            if (primary.value.parts[i].type === 'jps-curation-list') {
              let j = 0
              for (; j < primary.value.parts[i].parts.length; j++) {
                if (primary.value.parts[i].parts[j].id === id) {
                  primary.value.parts[i].parts.splice(j, 1)
                  return
                }
              }
            }
          }
        }
      }

      const isFavorite = (key: Key, id: string): boolean => {
        if (primary.value) {
          for (let i = 0; i < primary.value.parts.length; i++) {
            if (primary.value.parts[i].type === 'jps-curation-list') {
              for (let j = 0; j < primary.value.parts[i].parts.length; j++) {
                if (primary.value.parts[i].parts[j].id === id) return true
              }
            }
          }
        }
        return false
      }

      const getList = (note: Curation): PartList => {
        const listList = <PartList[]>(
          note.parts
            .filter((l) => l.type === 'jps-curation-list')
            .filter(
              (l) =>
                !l.parts.some(
                  (p) => p.type === 'jps-curation-list-search-result'
                )
            )
        )
        if (listList.length == 0) {
          const newList = createNewList()
          note.parts.push(newList)
          return newList
        } else {
          return listList[listList.length - 1]
        }
      }

      //////Search系
      const addSearch = async (name: string, q: SearchQuery) => {
        q.size = 5
        const tmpList = {
          cpid: luId(),
          type: 'jps-curation-list',
          viewType: 'bullet',
          csId: systemCrossSearchId(),
          openActionType: 'link',
          title: { ja: name },
          parts: [
            {
              type: 'jps-curation-list-search-result',
              dataType: 'item',
              query: q,
              csId: q.csid,
            },
          ],
        }
        primary.value.parts.push(tmpList)
        await saveNote(primary.value)
      }

      return {
        primary,
        list,
        clearNotes,
        createNote,
        getNote,
        changePrimary,
        createNewNoteByTitle,
        createNewNote,
        updateNote,
        deleteNote,
        addFavorite,
        removeFavorite,
        isFavorite,
        addSearch,
      }

    } else {

      const clearNotes = () => {}
      const createNote = (title: LangString): Curation => { return null }
      const getNote = (id: string): Promise<Curation> => { return null }
      const changePrimary = async (id: string): Promise<any> => { return null }
      const createNewNoteByTitle = async (title: LangString): Promise<Curation> => { return null }
      const createNewNote = async (note: Curation): Promise<Curation> => { return null }
      const updateNote = async (note: Curation): Promise<Curation> => { return null }
      const deleteNote = async (id: string): Promise<any> => { return null }
      const addFavorite = async (key: Key, id: string, image: ImageSelection) => {}
      const removeFavorite = async (key: Key, id: string) => {}
      const isFavorite = (key: Key, id: string): boolean => { return false }
      const addSearch = async (name: string, q: SearchQuery) => {}

      return {
        primary,
        list,
        clearNotes,
        createNote,
        getNote,
        changePrimary,
        createNewNoteByTitle,
        createNewNote,
        updateNote,
        deleteNote,
        addFavorite,
        removeFavorite,
        isFavorite,
        addSearch,
      }

    }

  },
  { persist: false },
)
